import type { PrototypeRuntime } from './prototypeTypes';
import { createInteractiveTurnstileProvider, createTurnstileTokenProvider } from './turnstileClient';
import type { TurnstileTokenProvider } from './turnstileClient';

export interface TurnstilePublicConfig {
	turnstileSiteKey: string | null;
	environment: string;
}

const requestGenerations = new WeakMap<object, number>();
const appliedFingerprints = new WeakMap<object, string>();

function uploadPurposeFromAction(action: string): 'feedback' | 'community' | 'ai' | null {
	if (action === 'uploads-presign-feedback') return 'feedback';
	if (action === 'uploads-presign-community') return 'community';
	if (action === 'uploads-presign-ai') return 'ai';
	return null;
}

function uploadDiagnosticCode(error: unknown): 'safety_timeout' | 'render_error' | 'challenge_timeout' | 'expired' | 'script_failed' | 'api_unavailable' | 'invalidated' | 'not_configured' | 'failed' | 'unknown' {
	const message = error instanceof Error ? error.message : '';
	if (message.includes('safety_timeout')) return 'safety_timeout';
	if (message.includes('render_error')) return 'render_error';
	if (message.includes('challenge_timeout')) return 'challenge_timeout';
	if (message.includes('expired')) return 'expired';
	if (message.includes('script_failed')) return 'script_failed';
	if (message.includes('api_unavailable')) return 'api_unavailable';
	if (message.includes('invalidated')) return 'invalidated';
	if (message.includes('not_configured')) return 'not_configured';
	if (message.startsWith('turnstile_challenge_failed')) return 'failed';
	return 'unknown';
}

function reportUploadTurnstileFailure(action: string, error: unknown, startedAt: number): void {
	const purpose = uploadPurposeFromAction(action);
	if (!purpose) return;
	void fetch('/api/uploads/diagnostics', {
		method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ phase: 'turnstile_client', purpose, errorCode: uploadDiagnosticCode(error), durationMs: Math.min(Math.max(Date.now() - startedAt, 0), 300_000) }),
	}).catch(() => undefined);
}

/** Applies public config with an explicit local-only development fallback. */
export function applyTurnstilePublicConfig(runtime: PrototypeRuntime, config: TurnstilePublicConfig): boolean {
	const environment = config.environment.trim().toLowerCase();
	const fingerprint = `${environment}:${config.turnstileSiteKey ?? ''}`;
	if (appliedFingerprints.get(runtime) === fingerprint) return Boolean(runtime.interactiveTurnstile);
	runtime.interactiveTurnstile?.removeWidget();
	const allowDevelopmentFallback = environment === 'local' || environment === 'development';
	runtime.turnstile = createTurnstileTokenProvider(config.turnstileSiteKey, allowDevelopmentFallback);
	runtime.interactiveTurnstile = createInteractiveTurnstileProvider(config.turnstileSiteKey, allowDevelopmentFallback);
	appliedFingerprints.set(runtime, fingerprint);
	return Boolean(runtime.interactiveTurnstile);
}

/** Reserves a public-config generation without mutating either provider. */
export function beginTurnstilePublicConfigRequest(runtime: PrototypeRuntime): number {
	const generation = (requestGenerations.get(runtime) ?? 0) + 1;
	requestGenerations.set(runtime, generation);
	return generation;
}

/** Consumes a previously fetched config only while it is still the newest request. */
export function applyTurnstilePublicConfigForGeneration(runtime: PrototypeRuntime, config: TurnstilePublicConfig, generation: number): boolean {
	if (requestGenerations.get(runtime) !== generation) return Boolean(runtime.interactiveTurnstile);
	return applyTurnstilePublicConfig(runtime, config);
}

/** Refetches public config so a failed bootstrap can recover without a page reload. */
export async function refreshTurnstileProviders(runtime: PrototypeRuntime): Promise<boolean> {
	const generation = beginTurnstilePublicConfigRequest(runtime);
	const config = await runtime.api.publicConfig();
	return applyTurnstilePublicConfigForGeneration(runtime, config, generation);
}

/** Retries an invisible action once when bootstrap never installed a provider. */
export async function getRecoverableTurnstileToken(runtime: PrototypeRuntime, action: string): Promise<string> {
	const startedAt = Date.now();
	try {
		return await runtime.turnstile.getToken(action);
	} catch (error) {
		if (!(error instanceof Error) || error.message !== 'turnstile_not_configured') {
			reportUploadTurnstileFailure(action, error, startedAt);
			throw error;
		}
		try {
			await refreshTurnstileProviders(runtime);
			return await runtime.turnstile.getToken(action);
		} catch (recoveryError) {
			reportUploadTurnstileFailure(action, recoveryError, startedAt);
			throw recoveryError;
		}
	}
}

/**
 * Prefetch (warm) an invisible Turnstile token so the first vote/comment is
 * instant instead of waiting 4-6s for a challenge solve. Call this on
 * community view mount. Non-fatal: silently skips if the provider doesn't
 * support prefetch or isn't configured yet.
 */
export function prefetchTurnstileToken(runtime: PrototypeRuntime, action: string): void {
	const provider = runtime.turnstile as TurnstileTokenProvider | undefined;
	if (!provider?.prefetchToken) return;
	try {
		provider.prefetchToken(action);
	} catch {
		// Prefetch failure is non-fatal; the real getToken() will retry.
	}
}

/**
 * Ends a route-owned invisible warm-up without touching a user-started
 * challenge. Consumers call this when their page unmounts or a visible
 * Turnstile surface takes foreground ownership.
 */
export function cancelTurnstilePrefetch(runtime: PrototypeRuntime | undefined, action: string): void {
	if (!runtime) return;
	try {
		runtime.turnstile?.cancelPrefetch?.(action);
	} catch {
		// Cancellation is best-effort. The next explicit action still obtains a
		// fresh token through the normal provider path.
	}
}

/**
 * Detect Turnstile client-side errors thrown by turnstileClient.ts /
 * turnstileLoader.ts. These are bare Error instances whose message starts
 * with "turnstile_" — they are NOT PrototypeApiError, so generic catch
 * blocks fall through to a confusing "verification unavailable" message.
 */
export function isTurnstileClientError(error: unknown): boolean {
	return error instanceof Error && error.message.startsWith('turnstile_');
}
